/**
 * StudioSidebar.js
 *
 * Left panel: "New Chat" button + project list with rename/delete actions.
 */
import React, { useState, useRef, useEffect } from 'react';
import {
  renameProject,
  deleteProject,
} from '../../services/studioProject.service';

function formatRelTime(iso) {
  if (!iso) return '';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1)  return 'just now';
    if (min < 60) return `${min}m ago`;
    const h = Math.floor(min / 60);
    if (h < 24)   return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch { return ''; }
}

function ProjectItem({ project, isActive, onSelect, onUpdated }) {
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [renaming,  setRenaming]  = useState(false);
  const [nameInput, setNameInput] = useState(project.name);
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  // Focus input when renaming
  useEffect(() => {
    if (renaming) inputRef.current?.focus();
  }, [renaming]);

  const commitRename = () => {
    const name = nameInput.trim() || project.name;
    renameProject(project.id, name);
    setRenaming(false);
    onUpdated();
  };

  const handleDelete = () => {
    if (window.confirm(`Delete "${project.name}"?`)) {
      deleteProject(project.id);
      onUpdated(project.id); // pass deleted id so parent can deselect
    }
    setMenuOpen(false);
  };

  // Last message preview
  const lastMsg = project.messages[project.messages.length - 1];
  const preview = lastMsg
    ? lastMsg.content.replace(/\*\*/g, '').slice(0, 55) + (lastMsg.content.length > 55 ? '…' : '')
    : 'No messages yet';

  return (
    <div
      className={`studio-project-item${isActive ? ' active' : ''}`}
      onClick={() => !renaming && onSelect(project.id)}
    >
      {renaming ? (
        <input
          ref={inputRef}
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenaming(false); }}
          onClick={(e) => e.stopPropagation()}
          style={{ width: '100%', fontSize: '0.84rem', fontWeight: 600,
                   border: '1px solid #6366f1', borderRadius: 4, padding: '2px 6px',
                   background: '#fff', color: '#0f172a', outline: 'none' }}
        />
      ) : (
        <>
          <div className="studio-project-name">{project.name}</div>
          <div className="studio-project-preview">{preview}</div>
          <div className="studio-project-time">{formatRelTime(project.updatedAt)}</div>
        </>
      )}

      {/* Three-dot menu */}
      {!renaming && (
        <div className="studio-project-menu" ref={menuRef} onClick={(e) => e.stopPropagation()}>
          <button className="studio-project-menu-btn" onClick={() => setMenuOpen(o => !o)}>
            ···
          </button>
          {menuOpen && (
            <div className="studio-project-dropdown">
              <button onClick={() => { setRenaming(true); setNameInput(project.name); setMenuOpen(false); }}>
                ✏️ Rename
              </button>
              <button className="danger" onClick={handleDelete}>
                🗑️ Delete
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StudioSidebar({ projects, currentId, onNew, onSelect, onUpdated }) {
  return (
    <div className="studio-sidebar">
      <div className="studio-sidebar-header">
        <button className="studio-new-btn" onClick={onNew}>
          <span style={{ fontSize: '1.1rem' }}>✦</span> New Chat
        </button>
      </div>

      <div className="studio-project-list">
        {projects.length === 0 && (
          <div style={{ padding: '20px 10px', fontSize: '0.8rem', color: '#94a3b8', textAlign: 'center' }}>
            No projects yet.<br />Click "New Chat" to start.
          </div>
        )}
        {projects.map(p => (
          <ProjectItem
            key={p.id}
            project={p}
            isActive={p.id === currentId}
            onSelect={onSelect}
            onUpdated={(deletedId) => onUpdated(deletedId)}
          />
        ))}
      </div>
    </div>
  );
}

export default StudioSidebar;
